import { type DocumentNode, gql } from '@apollo/client';
import { useQuery } from '@apollo/client/react';
import { Check, ChevronsUpDown } from 'lucide-react';
import { useEffect, useId, useState } from 'react';

import { Button } from '@/components/ui/button';
import {
  Command,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList
} from '@/components/ui/command';
import { Field, FieldLabel } from '@/components/ui/field';
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from '@/components/ui/popover';
import { cn } from '@/lib/utils';

const GET_ACCOUNTS_FOR_SELECT = gql`
  query GetAccountsForSelect($name: String, $first: Int) {
    accounts(name: $name, first: $first) {
      edges {
        node {
          id
          name
        }
      }
    }
  }
`;

const GET_CONTACTS_FOR_SELECT = gql`
  query GetContactsForSelect($name: String, $first: Int) {
    contacts(name: $name, first: $first) {
      edges {
        node {
          id
          name
        }
      }
    }
  }
`;

const GET_HR_EMPLOYEES_FOR_SELECT = gql`
  query GetHrEmployeesForSelect($name: String, $first: Int) {
    hrEmployees(name: $name, first: $first) {
      edges {
        node {
          id
          name
        }
      }
    }
  }
`;

const GET_ENTITY_NAME_BY_ID = gql`
  query GetEntityNameById($id: ID!) {
    node(id: $id) {
      ... on AccountNode {
        id
        accountName: name
      }
      ... on ContactNode {
        id
        contactName: name
      }
      ... on HrEmployeeNode {
        id
        employeeName: name
      }
    }
  }
`;

interface EntityEdge {
  node: EntityOption;
}

interface EntityOption {
  id: string;
  name: string;
}

type EntityType = 'account' | 'contact' | 'hrEmployee';

interface GetEntitiesData {
  accounts?: { edges: EntityEdge[] };
  contacts?: { edges: EntityEdge[] };
  hrEmployees?: { edges: EntityEdge[] };
}

interface GetEntityNameByIdData {
  node?: {
    accountName?: null | string;
    contactName?: null | string;
    employeeName?: null | string;
    id: string;
  } | null;
}

const ENTITY_CONFIG: Record<
  EntityType,
  {
    dataKey: keyof GetEntitiesData;
    placeholder: string;
    query: DocumentNode;
    searchPlaceholder: string;
  }
> = {
  account: {
    dataKey: 'accounts',
    placeholder: 'Select an account',
    query: GET_ACCOUNTS_FOR_SELECT,
    searchPlaceholder: 'Search accounts...'
  },
  contact: {
    dataKey: 'contacts',
    placeholder: 'Select a contact',
    query: GET_CONTACTS_FOR_SELECT,
    searchPlaceholder: 'Search contacts...'
  },
  hrEmployee: {
    dataKey: 'hrEmployees',
    placeholder: 'Select an employee',
    query: GET_HR_EMPLOYEES_FOR_SELECT,
    searchPlaceholder: 'Search employees...'
  }
};

interface EditEntitySelectFieldProps {
  entityType: EntityType;
  excludeId?: string;
  fieldClassName?: string;
  label: string;
  labelClassName?: string;
  onChange: (id: string, name: string) => void;
  onOpenChange?: (open: boolean) => void;
  placeholder?: string;
  required?: boolean;
  value: { id: string; name: string } | null;
}

export function EditEntitySelectField({
  entityType,
  excludeId,
  fieldClassName,
  label,
  labelClassName,
  onChange,
  onOpenChange,
  placeholder,
  required = false,
  value
}: EditEntitySelectFieldProps) {
  const [open, setOpen] = useState(false);
  const triggerId = useId();

  const handleOpenChange = (newOpen: boolean) => {
    setOpen(newOpen);
    onOpenChange?.(newOpen);
  };
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);

    return () => {
      clearTimeout(timer);
    };
  }, [searchTerm]);

  const config = ENTITY_CONFIG[entityType];

  const { data, loading } = useQuery<GetEntitiesData>(config.query, {
    fetchPolicy: 'cache-first',
    variables: {
      first: 100,
      name: debouncedSearchTerm || undefined
    }
  });
  const shouldFetchSelectedName = Boolean(value?.id && !value.name);
  const { data: selectedEntityData } = useQuery<GetEntityNameByIdData>(
    GET_ENTITY_NAME_BY_ID,
    {
      fetchPolicy: 'cache-first',
      skip: !shouldFetchSelectedName,
      variables: {
        id: value?.id ?? ''
      }
    }
  );
  const selectedEntityName =
    value?.name ??
    selectedEntityData?.node?.accountName ??
    selectedEntityData?.node?.contactName ??
    selectedEntityData?.node?.employeeName ??
    undefined;

  const entities: EntityOption[] =
    data?.[config.dataKey]?.edges
      .map((edge: EntityEdge) => edge.node)
      .filter((entity: EntityOption) => entity.id !== excludeId) ?? [];

  return (
    <Field className={cn('gap-1.5', fieldClassName)}>
      <FieldLabel
        className={labelClassName}
        htmlFor={triggerId}
      >
        {label}
        {required && <span className="text-red-500">*</span>}
      </FieldLabel>
      <Popover
        modal={true}
        onOpenChange={handleOpenChange}
        open={open}
      >
        <PopoverTrigger asChild>
          <Button
            aria-expanded={open}
            className={`dark:bg-input/30 dark:hover:bg-input/50 h-9 w-full justify-between bg-transparent font-normal ${!selectedEntityName ? 'text-gray-500' : ''}`}
            id={triggerId}
            role="combobox"
            variant="outline"
          >
            {selectedEntityName ?? placeholder ?? config.placeholder}
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          className="w-auto min-w-[var(--radix-popover-trigger-width)] p-0"
        >
          <Command shouldFilter={false}>
            <CommandInput
              onValueChange={setSearchTerm}
              placeholder={config.searchPlaceholder}
              value={searchTerm}
            />
            <CommandList>
              {loading ? (
                <div className="py-6 text-center text-sm text-gray-500">
                  Loading...
                </div>
              ) : (
                <CommandGroup>
                  <CommandItem
                    onSelect={() => {
                      onChange('', '');
                      setOpen(false);
                    }}
                    value="__none__"
                  >
                    <Check
                      className={cn(
                        'mr-2 h-4 w-4',
                        !value?.id ? 'opacity-100' : 'opacity-0'
                      )}
                    />
                    <span className="text-gray-500">None</span>
                  </CommandItem>
                  {entities.length === 0 ? (
                    <div className="py-4 text-center text-sm text-gray-500">
                      No{' '}
                      {entityType === 'hrEmployee'
                        ? 'HR employees'
                        : `${entityType}s`}{' '}
                      found.
                    </div>
                  ) : (
                    entities.map(entity => (
                      <CommandItem
                        key={entity.id}
                        onSelect={() => {
                          onChange(entity.id, entity.name);
                          setOpen(false);
                        }}
                        value={entity.id}
                      >
                        <Check
                          className={cn(
                            'mr-2 h-4 w-4',
                            value?.id === entity.id
                              ? 'opacity-100'
                              : 'opacity-0'
                          )}
                        />
                        {entity.name}
                      </CommandItem>
                    ))
                  )}
                </CommandGroup>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </Field>
  );
}
